import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
  Timestamp,
  serverTimestamp,
  doc,
  setDoc
} from "firebase/firestore";
import { db } from "../../services/firebase";
import { UserAuth } from "../../services/AuthContext";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import BackspaceTwoToneIcon from "@mui/icons-material/BackspaceTwoTone";
import Typography from "@mui/material/Typography";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Close";

import { EditToolbar } from "../../common/edittoolbar";

import {
  DataGrid,
  GridActionsCellItem,
  GridRowModes,
} from "@mui/x-data-grid";
import moment from "moment";


export const EditTrucks = () => {
  const [trucks, setTrucks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(true);
  const [rowModesModel, setRowModesModel] = useState({});
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
  });
  const navigate = useNavigate();
  const { user } = UserAuth();

  useEffect(() => {
    const fetchData = async () => {
      const q = query(
        collection(db, "trucks-p"),
        where("active", "==", active),
        orderBy("truckNumber")
      );
      const qs = await getDocs(q);

      const docs = [];

      qs.forEach((doc) => {
        docs.push({
          ...doc.data(),
          id: doc.id,
        });
      });

      setLoading(true);
      setTrucks(docs);
      setLoading(false);
    };

    fetchData();
  }, [active]);

  const columns = [
    { field: "truckNumber", headerName: "Truck Number", flex: .5, headerAlign: 'left', align:'left', type: "number", editable: true },
    { field: "truckDescription", headerName: "Truck Description", flex: 1, editable: true },
    { field: "truckCapacity", headerName: "Truck Capacity", flex: 1, headerAlign: 'left', align:'left', type: "number", editable: true },
    {
      field: "active",
      headerName: "Active",
      flex: 1,
      type: "boolean",
      editable: true,
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      cellClassName: "actions",
      getActions: ({ id }) => {
        //console.log(rowModesModel);
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<SaveIcon />}
              label="Save"
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label="Cancel"
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
            />,
          ];
        }

        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Delete"
            onClick={handleDeleteClick(id)}
            color="inherit"
          />,
        ];
      },
    },
  ];

  // https://codesandbox.io/s/0o2gny?file=/demo.js:2661-3076
  const handleRowEditStart = (params, event) => {
    event.defaultMuiPrevented = true;
  };

  const handleRowEditStop = (params, event) => {
    event.defaultMuiPrevented = true;
  };

  const handleEditClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleDeleteClick = (id) => async () => {
    //setRows(rows.filter((row) => row.id !== id));
    setLoading(true);
    //const done = await new Promise((res) => setTimeout(res, 2500));
    const deleted = {
      active: false,
      lastUpdatedBy: user.uid,
      lastUpdated: serverTimestamp()
    }

    const docRef = doc(db, 'trucks-p', id);
    await setDoc(docRef, deleted, {merge: true});
    setLoading(false);
    setTrucks(trucks.map(t => {
      if (t.id === id){
        return {...t, active: false};
      }
      return t;
    }));
  };

  const handleCancelClick = (id) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });
  };

  const handleNew = async () => {

    setLoading(true);
    //const done = await new Promise((res) => setTimeout(res, 2500));
    const newRef = doc(collection(db, "trucks-p"));
    console.log(newRef.id);
    const id = newRef.id;
    setLoading(false);

    setTrucks((oldRows) => [
      ...oldRows,
      {
        id,
        truckNumber: 0,
        truckDescription: "",
        truckCapacity: 0,
        active: true,
        lastUpdated: new Timestamp(),
        isNew: true,
      },
    ]);
    setRowModesModel((oldModel) => ({
      ...oldModel,
      [id]: { mode: GridRowModes.Edit, fieldToFocus: "truckDescription" },
    }));
  };

  const processRowUpdate = async (newRow) => {

    const updatedRow = { ...newRow, isNew: false };
    //setRows(rows.map((row) => (row.id === newRow.id ? updatedRow : row)));

    setLoading(true);
    //const done = await new Promise((res) => setTimeout(res, 2500));

    newRow.lastUpdatedBy = user.uid;
    newRow.lastUpdated = serverTimestamp();

    const docRef = doc(db, 'trucks-p', newRow.id);
    await setDoc(docRef, newRow, {merge: true});

    setLoading(false);

    return updatedRow;
  };

  const handleRowModesModelChange = (newRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  return (
    <Stack
      direction="column"
      justifyContent="center"
      alignItems="stretch"
      spacing={1}
    >
      <Box display="flex" justifyContent="space-between" p={2}>
        <Typography variant="h5">Manage Trucks</Typography>

        <Box display="flex">
          <Button
            variant="text"
            onClick={() => navigate('/maintenance')}
            startIcon={<BackspaceTwoToneIcon />}
          >
            Back
          </Button>
        </Box>
      </Box>
      <DataGrid
        loading={loading}
        autoHeight
        density="compact"
        pageSizeOptions={[5, 10, 25]}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        columns={columns}
        rows={trucks}
        editMode="row"
        rowModesModel={rowModesModel}
        onRowModesModelChange={handleRowModesModelChange}
        onRowEditStart={handleRowEditStart}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        slots={{
          toolbar: EditToolbar,
        }}
        slotProps={{
          toolbar: { setActive, handleNew, label: "Truck" },
        }}
      />
    </Stack>
  );
};